import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Button, Spinner } from "react-bootstrap";
import { FaEdit, FaPrint, FaArrowLeft } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const ViewQuotation = () => {
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotationDetails();
  }, [id]);

  const fetchQuotationDetails = async () => {
    try {
      setLoading(true);
      const response = await api.post("/quotation_detail.php", { id });
      
      if (response.data.head.code === 200) {
        const data = response.data.body;
        
        // Format the data for display
        const formattedQuotation = {
          id: data.id,
          quotationId: data.quotation_no || `QUO-${String(data.id).padStart(3, '0')}`,
          date: data.quotation_date,
          customer: data.customer_name,
          customerPhone: data.customer_mobile || "N/A",
          customerAddress: data.customer_address || "N/A",
          customerGST: data.customer_gst_no || "N/A",
          items: data.items.map(item => ({
            id: item.id,
            item_name: item.item_name,
            product_code: item.product_code,
            selling_rate: parseFloat(item.selling_rate) || 0,
            quantity: parseInt(item.quantity) || 0,
            total: parseFloat(item.total) || 0
          })),
          subTotal: parseFloat(data.sub_total) || 0,
          gstPercentage: parseFloat(data.gst_percentage) || 0,
          gstAmount: parseFloat(data.gst_amount) || 0,
          grandTotal: parseFloat(data.grand_total) || 0,
          status: data.status || "Draft"
        };
        
        setQuotation(formattedQuotation);
      } else {
        alert("Failed to fetch quotation: " + response.data.head.msg);
        navigate("/quotation-list");
      }
    } catch (error) {
      console.error("Error fetching quotation:", error);
      alert("Failed to load quotation. Please try again.");
      navigate("/quotation-list");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.open(`/sri_vinayaga_wires/quotationprint/${quotation.id}`, '_blank');
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Quotation Details</h4>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading quotation details...</p>
        </div>
      </Container>
    );
  }

  if (!quotation) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Quotation Details</h4>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
        <div className="text-center py-5">
          <p>Quotation not found.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Quotation Details</h4>
        <div className="d-flex gap-2">
          <Button className="btn-top" onClick={handlePrint}>
            <FaPrint className="me-2" /> Print
          </Button>
          <Button
            className="btn-submit"
            onClick={() => navigate(`/editquotation/${quotation.id}`)}
          >
            <FaEdit className="me-2" /> Edit
          </Button>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
      </div>

      {/* Quotation ID, Date and Mobile No Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Quotation ID:</strong>
              <div className="fs-5 text-primary">{quotation.quotationId}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Date:</strong>
              <div className="fs-5">{quotation.date}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Status:</strong>
              <div className="fs-5">
                <span className={`status-badge ${quotation.status === 'Accepted' ? 'status-approved' : 
                  quotation.status === 'Pending' ? 'status-pending' : 
                  quotation.status === 'Rejected' ? 'status-rejected' : 'status-default'}`}>
                  {quotation.status}
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Customer Details Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Customer Name:</strong>
              <div className="fs-5">{quotation.customer}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Mobile No:</strong>
              <div className="fs-5">{quotation.customerPhone}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>GST No:</strong>
              <div className="fs-5">{quotation.customerGST}</div>
            </div>
          </Col>
          <Col lg={12} md={12} sm={12}>
            <div className="mb-3">
              <strong>Address:</strong>
              <div className="fs-6">{quotation.customerAddress}</div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Quotation Items Table */}
      <div className="table-responsive">
        <Table bordered className="purchase-table">
          <thead className="table-dark">
            <tr>
              <th width="5%" className="text-center">S.No</th>
              <th width="45%">Product Name</th>
              <th width="15%">HSN Code</th>
              <th width="15%" className="text-center">Selling Rate</th>
              <th width="10%" className="text-center">Qty</th>
              <th width="10%" className="text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {quotation.items.map((item, index) => (
              <tr key={item.id || index}>
                <td className="text-center">{index + 1}</td>
                <td>{item.item_name}</td>
                <td className="text-center">{item.product_code}</td>
                <td className="text-center">₹{item.selling_rate.toFixed(2)}</td>
                <td className="text-center">{item.quantity}</td>
                <td className="text-center">₹{item.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Totals Section */}
      <Row className="mt-4">
        <Col xs={12} className="d-flex justify-content-end">
          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">₹{quotation.subTotal.toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">
                GST ({quotation.gstPercentage}%)
              </span>
              <span className="total-value">₹{quotation.gstAmount.toFixed(2)}</span>
            </div>
            <div className="total-row grand-total">
              <span className="total-label">Grand Total:</span>
              <span className="total-value">₹{quotation.grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end mt-4 gap-2">
        <Button className="btn-top" onClick={handlePrint}>
          <FaPrint className="me-2" /> Print Quotation
        </Button>
        <Button
          className="btn-submit"
          onClick={() => navigate(`/editquotation/${quotation.id}`)}
        >
          <FaEdit className="me-2" /> Edit Quotation
        </Button>
        <Button className="btn-top" onClick={() => navigate("/quotation-list")}>
          <FaArrowLeft className="me-2" /> Back to List
        </Button>
      </div>
    </Container>
  );
};

export default ViewQuotation;